// ABOUTME: Messages on their way, kept per item outside the window that sent them: closing the window loses none whose
// ABOUTME: answer is still to come, and opening it again shows them — failed ones to resend with the same id.
import type { ApolloClient } from "@apollo/client"
import {
  SendCalendarItemMessageDocument,
  type DiscussionMessageFragment,
  type TextFormat,
} from "@/graphql/main/gql/graphql"
import { serverErrorCode } from "@/graphql/client/errorMessage"
import { onUserSwitch } from "@/model/user/userSwitch"
import { sendableText } from "./messageText"
import type { AttachedAsset } from "./attachedAsset"
import { itemListeners } from "./itemListeners"

/**
 * A message on its way. Its text never changes once it is here: the server recognises a resend by
 * the clientMessageId before it looks at the text and answers the first message as stored, so
 * editing a message whose fate is unknown would show one text and keep another.
 */
export interface OutgoingMessage {
  clientMessageId: string
  body: string
  bodyFormat: TextFormat
  /** Uploaded already; sent by id, in this order, and shown under the text while the message is on its way. */
  attachments: readonly AttachedAsset[]
  /** `failed` — no answer, or one that does not tell: the message may be stored or not. */
  status: "sending" | "failed"
}

export interface MessageDraft {
  text: string
  format: TextFormat
  attachments: readonly AttachedAsset[]
}

/** The item's window while it is open: it takes the answers. */
export interface OutboxWindow {
  /** The stored message, from the answer: it goes into the feed under its own id and number. */
  onSent: (message: DiscussionMessageFragment) => void
  /** The server answered no: the text goes back to the writer with the reason. */
  onRefused: (message: OutgoingMessage, error: unknown) => void
  /**
   * The refused messages it can hand back whole; the rest wait for a window that can. A badge's preview holds
   * plain text only, and a message with attachments handed to it would lose them. Every one, when left out.
   */
  takes?: (message: OutgoingMessage) => boolean
}

const canTake = (window: OutboxWindow, message: OutgoingMessage) => window.takes?.(message) ?? true

/**
 * What the server refuses before it stores anything: the message did not go, and its text goes back
 * to be fixed. Anything else leaves the outcome unknown — no answer at all, or an internal failure
 * (`ErrSystemErrors`): the message is committed before its answer is put together
 * (gql-api internal/action/discussion/send.go:84-95), and that part can fail too. Only a resend with
 * the same clientMessageId can tell, and it never makes a second message.
 */
const REFUSED_BEFORE_STORING = new Set([
  "ErrActivityNotExits",
  "ErrAccessDenied",
  "ErrActivityTypeMismatch",
  "ErrImpersonationReadOnly",
  "ErrInvalidParameters",
  "ErrDiscussionMessageEmpty",
  "ErrDiscussionMessageTooLong",
  "ErrUnsupportedTextMarkup",
  "ErrDiscussionTooManyAttachments",
  "ErrDiscussionAttachmentUnusable",
  "ErrDiscussionQuotaExceeded",
])

const NONE: readonly OutgoingMessage[] = []
const outgoingByItem = new Map<string, readonly OutgoingMessage[]>()
/** Refusals that came while the item's window was closed: handed over when it opens. */
const refusedByItem = new Map<string, { message: OutgoingMessage; error: unknown }[]>()
const windows = new Map<string, OutboxWindow>()
const listeners = itemListeners()

export const outgoingOf = (itemId: string): readonly OutgoingMessage[] => outgoingByItem.get(itemId) ?? NONE

export const subscribeOutgoing = listeners.subscribe

function update(itemId: string, change: (list: readonly OutgoingMessage[]) => readonly OutgoingMessage[]): void {
  const next = change(outgoingOf(itemId))
  if (next.length === 0) outgoingByItem.delete(itemId)
  else outgoingByItem.set(itemId, next)
  listeners.notify(itemId)
}

const without = (itemId: string, clientMessageId: string) =>
  update(itemId, (list) => list.filter((message) => message.clientMessageId !== clientMessageId))

const withStatus = (itemId: string, clientMessageId: string, status: OutgoingMessage["status"]) =>
  update(itemId, (list) =>
    list.map((message) => (message.clientMessageId === clientMessageId ? { ...message, status } : message)),
  )

/** The window takes the answers while it is open, and the refusals that came while it was closed. */
export function openOutboxWindow(itemId: string, window: OutboxWindow): () => void {
  windows.set(itemId, window)
  const refused = refusedByItem.get(itemId) ?? []
  const left = refused.filter(({ message }) => !canTake(window, message))
  if (left.length > 0) refusedByItem.set(itemId, left)
  else refusedByItem.delete(itemId)
  refused
    .filter(({ message }) => canTake(window, message))
    .forEach(({ message, error }) => window.onRefused(message, error))
  return () => {
    if (windows.get(itemId) === window) windows.delete(itemId)
  }
}

/**
 * Counts the times every message was forgotten — another person signed in, or a test ended. A delivery begun before
 * the last one answers nobody: its late answer would reach a window of the person after, or of the next test.
 */
let resets = 0

async function deliver(client: ApolloClient, itemId: string, message: OutgoingMessage): Promise<void> {
  const begunAt = resets
  try {
    const { data } = await client.mutate({
      mutation: SendCalendarItemMessageDocument,
      variables: {
        input: {
          calendarItemId: itemId,
          clientMessageId: message.clientMessageId,
          body: message.body,
          bodyFormat: message.bodyFormat,
          attachmentIds: message.attachments.map((attachment) => attachment.id),
        },
      },
    })
    if (begunAt !== resets) return
    without(itemId, message.clientMessageId)
    // With the window closed, the feed opened next time reads the message from the server.
    if (data) windows.get(itemId)?.onSent(data.sendCalendarItemMessage.message)
  } catch (error: unknown) {
    if (begunAt !== resets) return
    const code = serverErrorCode(error)
    // A resend of a message its author deleted meanwhile: nothing is left to hand back.
    if (code === "ErrDiscussionMessageNotFound") {
      without(itemId, message.clientMessageId)
      return
    }
    if (code !== null && REFUSED_BEFORE_STORING.has(code)) {
      without(itemId, message.clientMessageId)
      const open = windows.get(itemId)
      if (open && canTake(open, message)) open.onRefused(message, error)
      else refusedByItem.set(itemId, [...(refusedByItem.get(itemId) ?? []), { message, error }])
      return
    }
    // The message may be stored or not: it waits, failed, for a resend with the same id.
    withStatus(itemId, message.clientMessageId, "failed")
  }
}

export function sendMessage(client: ApolloClient, itemId: string, draft: MessageDraft): void {
  const message: OutgoingMessage = {
    clientMessageId: crypto.randomUUID(),
    body: sendableText(draft.text),
    bodyFormat: draft.format,
    attachments: draft.attachments,
    status: "sending",
  }
  update(itemId, (list) => [...list, message])
  void deliver(client, itemId, message)
}

export function resendMessage(client: ApolloClient, itemId: string, clientMessageId: string): void {
  const message = outgoingOf(itemId).find((candidate) => candidate.clientMessageId === clientMessageId)
  if (!message) return
  withStatus(itemId, clientMessageId, "sending")
  void deliver(client, itemId, message)
}

export const discardMessage = (itemId: string, clientMessageId: string): void => without(itemId, clientMessageId)

/** Forgets every message on its way and every refusal kept, telling the windows that show them. */
function forgetEveryMessage(): void {
  resets += 1
  const items = [...outgoingByItem.keys()]
  outgoingByItem.clear()
  refusedByItem.clear()
  items.forEach(listeners.notify)
}

// Another person signed in on the tab — a sign-in after an expired session does not reload it: the messages the one
// before was sending, failed ones with their Retry, are theirs alone.
onUserSwitch(forgetEveryMessage)

/** Test seam — the outbox outlives a test file otherwise. Its windows go first: the test's own are unmounting, and
 * nothing the reset changes is theirs to hear. */
export function resetOutboxForTests(): void {
  windows.clear()
  listeners.clear()
  forgetEveryMessage()
}
